import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, iif, Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, shareReplay } from 'rxjs/operators';
import { FeatureService } from '@shared/services/feature.service';
import { ApiService } from './api.service';

/** Service that builds a session object and org observable */
@Injectable()
export class SessionService {
  public session = this.createSession();

  constructor(private readonly api: ApiService, private readonly feature: FeatureService) {}

  public createSession(): Observable<Session> {
    return forkJoin([this.createPubApiSession(), this.getDirectorySession(), this.getOrgPermissions()]).pipe(
      map(([publicApi, directory, orgPerms]) => ({
        ...directory,
        ...publicApi,
        ...{ orgPermissions: orgPerms }
      })),
      shareReplay(1)
    );
  }

  private getDirectorySession(): Observable<any> {
    const defaultSession = {
      user: { userType: 'trustee' }
    };

    return this.feature.hasFeature('ATEAM-1672_use-papi-legacy-session')
      .pipe(
        mergeMap((feature): Observable<any> =>
          iif(() => feature,
            this.api.get('/platform/api/v2/apps/legacy/session'),
            this.api
              .get(
                '/directory/api/v2/session?includeFieldConfigs=true&includeTranslations=true&includeRoleInfo=true'
              )
          )
        ),
        map((data) => data.res),
        map(({ namedPermissions, externalPermissions, ...results }) => results),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(defaultSession);
          } else {
            throwError(error);
          }
        })
      );
  }

  private createPubApiSession(): Observable<PubApiSession> {
    const defaultSession: PubApiSession = {
      permissions: [],
      organization: null,
      pubUser: null,
      products: []
    };

    return this.api
      .get(
        '/platform/api/v2/users/me?expand=authorization,orgproducts,organization,geolocation,station'
      )
      .pipe(
        map<any, PubApiSession>((me) => ({
          permissions: me?.authorization?.permissions?.sort() || [],
          organization: me.organization,
          pubUser: me,
          products: me.orgProducts.map((productObject) => productObject.id)
        })),
        catchError((error: HttpErrorResponse) => {
          window.logError('error creating session on public api', error);

          if (error.status === 404) {
            return of(defaultSession);
          }

          throwError(error);
        })
      );
  }

  private getOrgPermissions(): Observable<string[]> {

    return this.api
      .get(
        '/platform/api/v2/authorization/permissions',
        { pageSize: 9999 }
      )
      .pipe(
        map((response) =>
          (response?.entities || []).flatMap(
            ({ permissionMap }) =>
              Object.values(permissionMap || {})
                .flatMap((domainPermissions: any) =>
                  domainPermissions
                    .map(({ domain, entityType, action }) => [domain, entityType, action])
                    .filter((permissionTuple) =>
                      permissionTuple.every(
                        (permissionItem) => permissionItem && permissionItem.trim()
                      ))
              )
        )),
        catchError((error: HttpErrorResponse) => {
          window.logError('unable to get the orgnization permissions', error);

          return of([]);
        })
      )
  }
}

/** Basic type structure of the session object. There are currently undocumented properties that are not in use. */
export type Session = PubApiSession & AuthApiSession;

interface PubApiSession {
  organization?: Record<string, any>;
  permissions: string[];
  products: string[];
  pubUser?: Record<string, any>;
}

interface AuthApiSession {
  orgPermissions: string[][];
}
